import React, { useState } from 'react';
import SALESMAN_TRAINING, { TrainingDay } from '../data/salesmanTraining';

const BRAND   = '#4A7EB5';
const BG      = '#0F1923';
const SURFACE = '#1A2B3C';
const BORDER  = '#2D4259';
const MUTED   = '#8FA8C0';
const SUCCESS = '#10B981';
const PURPLE  = '#8B5CF6';

const DAY_LABELS = [
  { day: 1, title: 'Company Orientation',      icon: '🏢', note: 'Office · Instructor: Beckham' },
  { day: 2, title: 'Sales Training',           icon: '🚪', note: 'Office · Instructor: Beckham' },
  { day: 3, title: 'Roof & Product Knowledge', icon: '🏠', note: 'Office · Instructor: Beckham' },
  { day: 4, title: 'App & Systems Wrap',       icon: '📱', note: 'Office · Instructor: Beckham' },
  { day: 5, title: 'Field Ride-Along',         icon: '🚗', note: 'Field · With Beckham' },
];

const FORMAT_ICON: Record<string, string>  = { presentation: '🎤', video: '🎤', reading: '📖', quiz: '📝', activity: '🔨' };
const FORMAT_COLOR: Record<string, string> = { presentation: PURPLE, video: PURPLE, reading: BRAND, quiz: '#F59E0B', activity: SUCCESS };
const FORMAT_LABEL: Record<string, string> = { presentation: 'In-Person', video: 'In-Person', reading: 'Reading', quiz: 'Quiz', activity: 'Activity' };

export default function TrainingTrackerPage() {
  const days: TrainingDay[] = SALESMAN_TRAINING;
  const [activeDay, setActiveDay] = useState(1);
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded]   = useState<Record<number, boolean>>({ 0: true });

  const toggleComplete = (moduleId: string, index: number) => {
    const nowComplete = !completed[moduleId];
    setCompleted(prev => ({ ...prev, [moduleId]: nowComplete }));
    if (nowComplete) {
      // Auto-open next module
      setExpanded((prev: Record<number, boolean>) => ({ ...prev, [index]: false, [index + 1]: true }));
    }
  };

  const resetDay = () => {
    const moduleIds = currentModules.map((_: any, i: number) => `day${activeDay}_${i}`);
    setCompleted(prev => {
      const next = { ...prev };
      moduleIds.forEach((id: string) => delete next[id]);
      return next;
    });
    setExpanded({ 0: true });
  };

  const currentModules = (days.find(d => d.day === activeDay)?.modules || []).map((m: any, i: number) => ({
    ...m, moduleId: `day${activeDay}_${i}`,
  }));

  const completedCount  = currentModules.filter((m: any) => completed[m.moduleId]).length;
  const totalCount      = currentModules.length;
  const dayComplete     = totalCount > 0 && completedCount === totalCount;
  const firstIncomplete = currentModules.findIndex((m: any) => !completed[m.moduleId]);
  const totalDuration   = currentModules.reduce((s: number, m: any) => s + (m.duration || 0), 0);

  return (
    <div style={{ display: 'flex', height: '100vh', background: BG, fontFamily: 'Arial, sans-serif', overflow: 'hidden' }}>

      {/* ── Left Sidebar — Day Nav ────────────────────────────────── */}
      <div style={{ width: 220, background: '#0a1118', borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '22px 18px 16px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>Training Tracker</div>
          <div style={{ color: MUTED, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>First-Week Itinerary</div>
        </div>

        <nav style={{ flex: 1, padding: '12px 8px' }}>
          {DAY_LABELS.map(dl => {
            const isActive = activeDay === dl.day;
            // Check if all modules for this day are complete
            const dayMods = (days.find(d => d.day === dl.day)?.modules || []).map((_: any, i: number) => `day${dl.day}_${i}`);
            const isDone = dayMods.length > 0 && dayMods.every((id: string) => completed[id]);

            return (
              <button key={dl.day} onClick={() => { setActiveDay(dl.day); setExpanded({ 0: true }); }} style={{
                width: '100%', textAlign: 'left',
                background: isActive ? BRAND + '22' : 'transparent',
                border: `1px solid ${isActive ? BRAND + '55' : 'transparent'}`,
                borderRadius: 10, padding: '11px 14px', cursor: 'pointer', marginBottom: 4,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 20 }}>{dl.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: isActive ? '#fff' : isDone ? SUCCESS : MUTED, fontSize: 13, fontWeight: isActive ? 700 : 400 }}>
                    Day {dl.day}
                  </div>
                  <div style={{ color: isDone ? SUCCESS : isActive ? MUTED : '#4A6580', fontSize: 11, marginTop: 1 }}>
                    {isDone ? '✓ Complete' : dl.title}
                  </div>
                </div>
                {isDone && <span style={{ color: SUCCESS, fontSize: 14 }}>✓</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Main Content ─────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: '#0a1118', borderBottom: `1px solid ${BORDER}`, padding: '16px 28px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 28 }}>{DAY_LABELS.find(d => d.day === activeDay)?.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 800 }}>
                Day {activeDay} — {DAY_LABELS.find(d => d.day === activeDay)?.title}
              </div>
              <div style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>
                {DAY_LABELS.find(d => d.day === activeDay)?.note}
              </div>
            </div>

            {/* Progress indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: dayComplete ? SUCCESS : '#fff', fontWeight: 800, fontSize: 18 }}>
                  {completedCount}/{totalCount}
                </div>
                <div style={{ color: MUTED, fontSize: 11 }}>modules · ~{totalDuration} min</div>
              </div>
              {completedCount > 0 && (
                <button onClick={resetDay} style={{
                  background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`,
                  borderRadius: 8, padding: '6px 12px', fontSize: 11, cursor: 'pointer',
                }}>
                  Reset Day
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ height: 4, background: '#2D4259', borderRadius: 2, overflow: 'hidden', marginTop: 14 }}>
            <div style={{
              height: 4, borderRadius: 2, transition: 'width 0.3s',
              width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%`,
              background: dayComplete ? SUCCESS : BRAND,
            }} />
          </div>
        </div>

        {/* Module List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>

          {/* Day Complete Banner */}
          {dayComplete && (
            <div style={{ background: SUCCESS + '18', border: `1px solid ${SUCCESS}44`, borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 28 }}>🎉</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: SUCCESS, fontWeight: 800, fontSize: 16 }}>Day {activeDay} Complete!</div>
                <div style={{ color: MUTED, fontSize: 13, marginTop: 2 }}>
                  {activeDay < 5 ? `All modules covered. Move on to Day ${activeDay + 1} when the group is ready.` : 'Full training week complete. Group is ready for the field! 🚗'}
                </div>
              </div>
              {activeDay < 5 && (
                <button onClick={() => { setActiveDay(activeDay + 1); setExpanded({ 0: true }); }} style={{
                  background: SUCCESS, color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 20px', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                }}>
                  Day {activeDay + 1} →
                </button>
              )}
            </div>
          )}

          {/* Modules */}
          {currentModules.map((m: any, i: number) => {
            const isOpen     = !!expanded[i];
            const isDone     = !!completed[m.moduleId];
            const isUpNext   = !isDone && i === firstIncomplete;
            const fmtColor   = FORMAT_COLOR[m.format] || MUTED;

            return (
              <div key={i} style={{
                marginBottom: 10,
                border: `1px solid ${isDone ? SUCCESS + '55' : isUpNext ? BRAND + '88' : BORDER}`,
                borderRadius: 14, overflow: 'hidden',
                background: isDone ? SUCCESS + '08' : isUpNext ? BRAND + '08' : SURFACE,
                boxShadow: isUpNext ? `0 0 0 2px ${BRAND}22` : 'none',
              }}>

                {/* Header Row */}
                <div style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>

                  {/* Checkbox */}
                  <button onClick={() => toggleComplete(m.moduleId, i)} style={{
                    width: 26, height: 26, borderRadius: 13, flexShrink: 0, cursor: 'pointer',
                    background: isDone ? SUCCESS : 'transparent',
                    border: `2px solid ${isDone ? SUCCESS : isUpNext ? BRAND : BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
                  }}>
                    {isDone
                      ? <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>
                      : isUpNext
                      ? <span style={{ width: 9, height: 9, borderRadius: 5, background: BRAND, display: 'block' }} />
                      : null}
                  </button>

                  {/* Title + meta */}
                  <button onClick={() => setExpanded((prev: Record<number, boolean>) => ({ ...prev, [i]: !prev[i] }))} style={{
                    flex: 1, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 17 }}>{FORMAT_ICON[m.format] || '📄'}</span>
                      <span style={{
                        color: isDone ? '#64748b' : '#fff', fontWeight: 700, fontSize: 14,
                        textDecoration: isDone ? 'line-through' : 'none',
                      }}>{m.title}</span>
                      <span style={{ background: fmtColor + '22', color: fmtColor, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6, textTransform: 'uppercase' }}>
                        {FORMAT_LABEL[m.format] || m.format}
                      </span>
                      {m.required && <span style={{ background: '#EF444422', color: '#EF4444', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6 }}>Required</span>}
                      {isUpNext && <span style={{ background: BRAND + '33', color: BRAND, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6 }}>▶ Now</span>}
                    </div>
                    <div style={{ color: MUTED, fontSize: 11, marginTop: 3 }}>⏱ {m.duration} min{m.passScore ? ` · Pass: ${m.passScore}%` : ''}</div>
                  </button>

                  {/* Mark Done button */}
                  {!isDone && (
                    <button onClick={() => toggleComplete(m.moduleId, i)} style={{
                      background: isUpNext ? SUCCESS : 'transparent',
                      color: isUpNext ? '#fff' : MUTED,
                      border: `1px solid ${isUpNext ? SUCCESS : BORDER}`,
                      borderRadius: 8, padding: '7px 16px', fontWeight: 700, fontSize: 12,
                      cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                      ✓ Done
                    </button>
                  )}

                  {/* Expand arrow */}
                  <span onClick={() => setExpanded((prev: Record<number, boolean>) => ({ ...prev, [i]: !prev[i] }))}
                    style={{ color: MUTED, fontSize: 18, cursor: 'pointer', flexShrink: 0, display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›
                  </span>
                </div>

                {/* Expanded Content */}
                {isOpen && (
                  <div style={{ borderTop: `1px solid ${BORDER}`, padding: '18px 20px' }}>

                    {(m.format === 'presentation' || m.format === 'video') && m.talkingPoints && (
                      <div>
                        <div style={{ color: PURPLE, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>🎤 Talking Points</div>
                        <div style={{ background: BG, border: `1px solid ${PURPLE}33`, borderRadius: 10, padding: '16px 18px', color: '#cbd5e1', fontSize: 14, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
                          {m.talkingPoints}
                        </div>
                        <div style={{ marginTop: 10, background: PURPLE + '11', border: `1px solid ${PURPLE}33`, borderRadius: 8, padding: '10px 14px', color: MUTED, fontSize: 12 }}>
                          🎤 Present this to the group live. Take questions, then hit <strong style={{ color: SUCCESS }}>✓ Done</strong>.
                        </div>
                      </div>
                    )}

                    {(m.format === 'reading' || m.format === 'activity') && m.content && (
                      <div>
                        <div style={{ color: fmtColor, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                          {m.format === 'activity' ? '🔨 Activity' : '📖 Content'}
                        </div>
                        <div style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.85 }} dangerouslySetInnerHTML={{ __html: m.content }} />
                      </div>
                    )}

                    {m.format === 'quiz' && m.questions && (
                      <div>
                        <div style={{ color: '#F59E0B', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                          📝 Quiz — {m.questions.length} Questions · Pass: {m.passScore}%
                        </div>
                        {m.questions.map((q: any, qi: number) => (
                          <div key={qi} style={{ marginBottom: 10, background: BG, borderRadius: 10, border: `1px solid ${BORDER}`, padding: '12px 16px' }}>
                            <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Q{qi + 1}: {q.q}</div>
                            {q.options.map((opt: string, oi: number) => (
                              <div key={oi} style={{
                                padding: '6px 10px', borderRadius: 6, marginBottom: 4, fontSize: 13,
                                background: oi === q.answer ? SUCCESS + '22' : 'transparent',
                                color: oi === q.answer ? SUCCESS : MUTED,
                                border: `1px solid ${oi === q.answer ? SUCCESS + '44' : 'transparent'}`,
                                fontWeight: oi === q.answer ? 700 : 400,
                              }}>
                                {oi === q.answer ? '✓ ' : '○ '}{opt}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                    {!isDone && (
                      <button onClick={() => toggleComplete(m.moduleId, i)} style={{
                        marginTop: 16, width: '100%', background: SUCCESS, color: '#fff',
                        border: 'none', borderRadius: 10, padding: '12px', fontWeight: 800,
                        fontSize: 14, cursor: 'pointer',
                      }}>
                        ✓ Mark Complete & Continue
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
